import { memo } from 'react';

export type CoverSize = 'xs' | 'sm' | 'md' | 'lg';

export interface CoverSource {
  bookId?: string;
  isbn13?: string;
  isbn10?: string;
  olid?: string; // Open Library ID
  title?: string;
  author?: string;
  coverUrl?: string; // Original cover URL from enriched data
  coverCacheUrl?: string; // Cached cover URL from our storage
}

// Size mappings for dimensions and vendor parameters with 2x srcsets
export const COVER_DIMENSIONS = {
  xs: { width: 56, height: 84, container: 'w-14 h-21' },
  sm: { width: 72, height: 108, container: 'w-18 h-27' },
  md: { width: 96, height: 144, container: 'w-24 h-36' },
  lg: { width: 120, height: 180, container: 'w-30 h-45' },
} as const;

// Cache for resolved URLs (in-memory for session)
const urlCache = new Map<string, string>();

/**
 * Generate cache key for URL resolution
 * Uses multiple discriminators to ensure unique keys per book
 */
function getCacheKey(source: CoverSource, size: CoverSize): string {
  const parts = [
    source.bookId,
    source.isbn13,
    source.isbn10,
    source.olid,
    source.coverCacheUrl,
    source.coverUrl,
    source.title && source.author ? `${source.title}|${source.author}` : source.title
  ].filter(Boolean);

  // If no stable discriminators exist, disable caching for this case
  if (parts.length === 0) {
    return '';
  }

  parts.push(size);
  return parts.join('|');
}

/**
 * Resolve cover URL with proper fallback chain
 * Priority: cached URL → proxy (for English enforcement) → original URL → title/author fallback
 */
export function resolveCoverUrl(source: CoverSource, size: CoverSize = 'md'): string | null {
  const cacheKey = getCacheKey(source, size);
  
  // Check cache first (only if we have a valid cache key)
  if (cacheKey && urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey) || null;
  }

  let resolvedUrl: string | null = null;

  // CRITICAL OPTIMIZATION: Priority 1 - Use cached cover URL directly if available
  // This completely bypasses the proxy and loads instantly from Supabase Storage
  if (source.coverCacheUrl) {
    resolvedUrl = source.coverCacheUrl;
    // Cache this for future renders
    if (cacheKey) {
      urlCache.set(cacheKey, resolvedUrl);
    }
    return resolvedUrl;
  }
  
  // Priority 2: Use proxy endpoint for ISBN/OLID (enforces English-only filtering)
  else if (source.isbn13 || source.isbn10 || source.olid) {
    const params = new URLSearchParams();
    
    if (source.bookId) params.set('bookId', source.bookId);
    if (source.isbn13) params.set('isbn13', source.isbn13);
    if (source.isbn10) params.set('isbn10', source.isbn10);
    if (source.olid) params.set('olid', source.olid);
    params.set('size', size);
    
    // CRITICAL: For OLID-only requests, prefer Google Books (English-verified)
    if (source.olid && !source.isbn13 && !source.isbn10) {
      params.set('prefer', 'gb');
      // Always include title/author for OLID-only to enable GB title search
      if (source.title) params.set('title', source.title);
      if (source.author) params.set('author', source.author);
      console.log('[CoverUtils] OLID-only request, preferring Google Books with title/author');
    }
    
    // Add cache busting based on source data
    const etag = source.coverCacheUrl ? 'cached' : 'live';
    params.set('v', etag);

    resolvedUrl = `https://zjubnjpiajoscjmogyqb.supabase.co/functions/v1/covers?${params.toString()}`;
  }
  // Priority 3: Use original cover URL only if no identifiers (legacy/direct URLs)
  else if (source.coverUrl) {
    resolvedUrl = source.coverUrl;
  }
  // Priority 4: Title/author fallback for books without identifiers (uses Google/OpenLibrary by title)
  else if (source.title && source.author && source.bookId) {
    const params = new URLSearchParams();
    params.set('bookId', source.bookId);
    params.set('title', source.title);
    params.set('author', source.author);
    params.set('size', size);
    params.set('fallback', 'true');
    // Explicit version token to avoid stale caches when switching to fallback mode
    params.set('v', 'fb');
    
    resolvedUrl = `https://zjubnjpiajoscjmogyqb.supabase.co/functions/v1/covers?${params.toString()}`;
    
    // Trigger enrichment in background for future requests
    if (import.meta.env.DEV) {
      triggerBookEnrichment(source.bookId).catch(error => 
        console.warn('[CoverUtils] Background enrichment failed:', error)
      );
    }
  }
  
  // Cache the result (only if we have a valid cache key)
  if (cacheKey && resolvedUrl) {
    urlCache.set(cacheKey, resolvedUrl);
  }
  
  return resolvedUrl;
}

/**
 * Trigger background book enrichment for missing metadata
 */
async function triggerBookEnrichment(bookId: string): Promise<void> {
  try {
    // Dynamic import to avoid circular dependencies
    const { enrichSpecificBook } = await import('./enrichmentUtils');
    await enrichSpecificBook(bookId);
    console.log(`[CoverUtils] Triggered enrichment for book ${bookId}`);
  } catch (error) {
    console.warn(`[CoverUtils] Failed to trigger enrichment for book ${bookId}:`, error);
  }
}

/**
 * Generate srcset for high DPI displays using our proxy
 */
export function generateSrcSet(baseUrl: string, size: CoverSize): string {
  // For proxy URLs, generate 2x version by changing size parameter
  if (baseUrl.includes('/functions/v1/covers?')) {
    const url1x = baseUrl;
    
    // For 2x, use larger size
    const sizeUpgrade: Record<CoverSize, CoverSize> = {
      xs: 'sm',
      sm: 'md', 
      md: 'lg',
      lg: 'lg' // lg is max
    };
    
    const largerSize = sizeUpgrade[size];
    const url2x = baseUrl.replace(`size=${size}`, `size=${largerSize}`);
    
    return `${url1x} 1x, ${url2x} 2x`;
  }
  
  // Fallback for direct URLs
  return `${baseUrl} 1x`;
}

/**
 * Create optimized alt text for cover images
 */
export function createAltText(source: CoverSource): string {
  if (source.title && source.author) {
    return `Cover of ${source.title} by ${source.author}`;
  }
  if (source.title) {
    return `Cover of ${source.title}`;
  }
  return 'Book cover';
}

/**
 * Debug helper for development (guards with DEV check)
 */
export function logCoverDebug(source: CoverSource, url: string | null, size: CoverSize) {
  if (import.meta.env.DEV && window.location.search.includes('debug=covers')) {
    console.log('[CoverDebug]', {
      source,
      resolvedUrl: url,
      size,
      cacheSize: urlCache.size
    });
  }
}

/**
 * Clear cover URL cache (useful for testing)
 */
export function clearCoverCache() {
  urlCache.clear();
}

/**
 * Memoized cover URL resolver for React components
 */
export const useCoverUrl = memo((source: CoverSource, size: CoverSize = 'md') => {
  return resolveCoverUrl(source, size);
});